import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Icon, Tab } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import { FormFields } from '~/components/FormFields'
import { StateEl } from '../style'
import { GlobalEl } from '~/styles/app'
import notify from '~/components/Notify'
import Drawer from '~/components/Drawer'
import ENV from '~/environment'
import { useAuth } from '~/routes/components/AuthProvider'
import { KeyLabelType } from '~/types'
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style'
import { StyledMenuItem } from '~/pages/Dairy/CloudStorage/style'
import FileUpload from '~/components/FileUpload'
import Dialog from '~/components/Dialog'
import axios from 'axios'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import DairyService from '~/api/services/dairy'
import { StyledFileSection } from './style'

const ACCEPTED_TYPES = { 'application/pdf': ['.pdf'] }
const ACCEPTED_MIME_TYPES = ['pdf']

interface Props {
  isVisible: boolean
  handleHiddenDrawer: () => void
  months: KeyLabelType[]
  fetchData: () => void
  fileId?: string | null
}

const CreateEditSegmentationFile: React.FC<Props> = ({ isVisible, handleHiddenDrawer, months, fetchData, fileId }) => {
  const { t } = useTranslation()
  const { systemData: { languages } } = useAuth()
  const { firstColor } = useContext(ThemeContext)

  const [isBusy, setIsBusy] = useState(false)
  const [existingFiles, setExistingFiles] = useState<Record<number, string>>({})
  const [selectedFileNames, setSelectedFileNames] = useState<Record<number, string>>({})
  const [selectedFiles, setSelectedFiles] = useState<Record<number, File>>({})
  const [previewBusy, setPreviewBusy] = useState<Record<number, boolean>>({})
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)

  const isEditMode = Boolean(fileId)

  const { handleSubmit, control, reset, formState, setValue } = useForm({ mode: 'onSubmit' })

  useEffect(() => {
    if (isVisible) {
      reset({
        month: '',
        languages: languages.map(lang => ({ id: lang.id, title: '' }))
      })
      setExistingFiles({})
      setSelectedFileNames({})
      setSelectedFiles({})
    }
  }, [isVisible])

  useEffect(() => {
    if (isVisible && fileId) {
      loadEditData()
    }
  }, [fileId, isVisible])

  async function loadEditData() {
    setIsBusy(true)
    try {
      const response = await DairyService.editSegmentationFile(fileId)
      const editData = response.data
      const filesMap: Record<number, string> = {}
      const langDefaults = languages.map(lang => {
        const langData = editData.languages?.find((l: any) => l.id === lang.id) || {}
        if (langData.filename) filesMap[lang.id] = langData.filename
        return { id: lang.id, title: langData.title || '' }
      })
      setExistingFiles(filesMap)
      reset({ month: editData.month || '', languages: langDefaults })
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsBusy(false)
    }
  }

  async function handlePreviewExisting(langId: number) {
    const fileName = existingFiles[langId]
    if (!fileName) return
    setPreviewBusy(prev => ({ ...prev, [langId]: true }))
    try {
      const response = await DairyService.getSegmentationFileByLang(fileId, fileName)
      const type = response.data.split(';')[0].split('data:')[1]
      const blob = b64toBlob(response.data, type);
      const blobUrl = URL.createObjectURL(blob);
      setPreviewSrc(blobUrl);
    } catch (error) {
      handleErrors(error)
    } finally {
      setPreviewBusy(prev => ({ ...prev, [langId]: false }))
    }
  }

  function handlePreviewSelected(langId: number) {
    const file = selectedFiles[langId]
    if (!file) return
    const fileSrc = URL.createObjectURL(file)
    setPreviewSrc(fileSrc)
  }

  const onSubmit = async (data) => {
    const formData = new FormData()
    formData.append('month', data.month)
    data.languages.forEach(language => {
      if (language.file) {
        formData.append(`file-${language.id}`, language.file)
        delete language.file
      }
    })
    formData.append('languages', JSON.stringify(data.languages))
    setIsBusy(true)
    try {
      const url = isEditMode
        ? `${ENV.API_URL}/segmentation/files/${fileId}/update`
        : `${ENV.API_URL}/segmentation/files/store`
      await axios.post(url, formData, {
        headers: { 'Authorization': localStorage.getItem('token') }
      })
      reset()
      notify(isEditMode ? t('Successfully updated segmentation file') : t('Successfully created segmentation file'), 'success')
      fetchData()
      handleHiddenDrawer()
    } catch (err) {
      handleErrors(err)
    } finally {
      setIsBusy(false)
    }
  }

  const monthOptions = months.map(m => ({ key: m.key, value: m.key, text: m.label }))

  function getMenuItem(label, index) {
    // @ts-ignore
    const errors = formState.errors?.languages?.map((lang, i) => i)?.filter(item => typeof item === 'number') || []
    return <StyledMenuItem key={label} className='tab-item'>
      <div className={errors.includes(index) ? 'error' : ''}>{label}</div>
    </StyledMenuItem>
  }

  function renderFileSection(language, indexLanguage) {
    const existingFilename = existingFiles[language.id]
    const selectedFilename = selectedFileNames[language.id]
    const displayedFilename = selectedFilename || existingFilename
    const hasSelected = Boolean(selectedFilename)
    const isClickable = Boolean(displayedFilename) && !previewBusy[language.id]

    function handleFilenameClick() {
      if (hasSelected) return handlePreviewSelected(language.id)
      if (!previewBusy[language.id]) return handlePreviewExisting(language.id)
    }

    return <StyledFileSection $primaryColor={firstColor}>
      <div className='file-icon'>
        <Icon name='file pdf outline' size='large' />
      </div>
      <div className='file-info'>
        <div className='file-label'>{t('File')}</div>
        {displayedFilename ? (
          <div
            className={`file-name${isClickable ? ' clickable' : ''}`}
            onClick={isClickable ? handleFilenameClick : undefined}
          >
            {displayedFilename}
            {!hasSelected && previewBusy[language.id] && ' ...'}
          </div>
        ) : (
          <div className='file-placeholder'>{t('No file selected')}</div>
        )}
      </div>
      <div className='file-actions'>
        <FileUpload
          type='button'
          accept={ACCEPTED_TYPES}
          mimeTypes={ACCEPTED_MIME_TYPES}
          sizeLimitInMB={10}
          onChange={(file: any) => {
            setValue(`languages[${indexLanguage}].file`, file)
            setSelectedFileNames(prev => ({ ...prev, [language.id]: file?.name || '' }))
            setSelectedFiles(prev => ({ ...prev, [language.id]: file }))
          }}
        />
      </div>
    </StyledFileSection>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={languages.map((language) => {
        const indexLanguage = languages.findIndex(l => l.id === language.id)
        return {
          menuItem: getMenuItem(language.label, indexLanguage),
          pane: <StyledTabPane key={language.id}>
            <FormFields.Input
              control={control}
              name={`languages[${indexLanguage}].title`}
              label={t('Title')}
              placeholder={t('Title')}
              rules={{ required: { value: true, message: t('Title is required') } }}
            />
            {renderFileSection(language, indexLanguage)}
          </StyledTabPane>
        }
      })}
    />
  }

  const renderContent = () => (
    <>
      <FormFields.Select
        control={control}
        name='month'
        label={t('Month')}
        options={monthOptions}
        placeholder={t('Select month')}
        rules={{ required: { value: true, message: t('Month is required') } }}
      />
      {renderLanguageFields()}
      <StateEl.DrawerFooter>
        <GlobalEl.PrimaryButton disabled={isBusy}>
          {isEditMode ? t('Save') : t('Add')}
        </GlobalEl.PrimaryButton>
      </StateEl.DrawerFooter>
    </>
  )

  return <>
    <Dialog
      type='preview'
      isDialogOpen={Boolean(previewSrc)}
      textHeader={t('Preview')}
      textContent={<iframe width='100%' height='100%' src={previewSrc} style={{ border: 'none' }} />}
      handleClose={() => setPreviewSrc(null)}
      textClose={t('Close')}
    />
    <Drawer
      isVisible={isVisible}
      handleSubmit={handleSubmit(onSubmit)}
      isBusy={isBusy}
      header={isEditMode ? t('Edit segmentation file') : t('Create segmentation file')}
      closeOnEscape
      minWidth={'450px'}
      onHide={() => {
        reset()
        setExistingFiles({})
        setSelectedFileNames({})
        setSelectedFiles({})
        handleHiddenDrawer()
      }}
      content={renderContent()}
    />
  </>
}

export default CreateEditSegmentationFile
